import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { memberService } from '@/lib/api/services/member'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Member,
  CreateMember,
  UpdateMember,
  UpdateMemberStatus,
  MemberFilterParams,
  MemberCategory,
  MemberStats,
  MemberStatsResponse,
  MembersCategoryCount
} from '@/lib/api/types'

// Query keys for caching
export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (filters?: MemberFilterParams) => [...memberKeys.lists(), filters] as const,
  details: () => [...memberKeys.all, 'detail'] as const,
  detail: (id: string) => [...memberKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: MemberFilterParams) =>
    [...memberKeys.all, 'filtered', params, filters] as const,
  search: (query: string, params?: PaginationParams) => [...memberKeys.all, 'search', query, params] as const,
  categories: () => [...memberKeys.all, 'categories'] as const,
  stats: () => [...memberKeys.all, 'stats'] as const,
  membersCategoryCount: () => [...memberKeys.all, 'membersCategoryCount'] as const
}

// Hook to fetch all members
export function useAllMembers() {
  return useQuery<ApiResponse<Member[]>, Error>({
    queryKey: memberKeys.lists(),
    queryFn: () => memberService.getAllMembers()
  })
}

// Hook to filter members with pagination
export function useFilterMembers(
  params: PaginationParams,
  filters: MemberFilterParams,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<Member>>, Error>({
    queryKey: memberKeys.filtered(params, filters),
    queryFn: () => memberService.filterMembers(params, filters),
    enabled: enabled
  })
}

// Hook to fetch a single member
export function useMember(memberId: string) {
  return useQuery<ApiResponse<Member>, Error>({
    queryKey: memberKeys.detail(memberId),
    queryFn: () => memberService.getMemberById(memberId),
    enabled: !!memberId
  })
}

// Hook to create a member
export function useCreateMember() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Member>, Error, CreateMember>({
    mutationFn: (data) => memberService.createMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() })
      queryClient.invalidateQueries({ queryKey: memberKeys.all })
      queryClient.invalidateQueries({ queryKey: memberKeys.stats() })
    }
  })
}

// Hook to update a member
export function useUpdateMember() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Member>,
    Error,
    { memberId: string; data: UpdateMember }
  >({
    mutationFn: ({ memberId, data }) =>
      memberService.updateMember(memberId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() })
      queryClient.invalidateQueries({ queryKey: memberKeys.all })
      queryClient.invalidateQueries({ queryKey: memberKeys.detail(variables.memberId) })
      queryClient.invalidateQueries({ queryKey: memberKeys.stats() })
    }
  })
}

// Hook to update member status
export function useUpdateMemberStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { memberId: string; data: UpdateMemberStatus }
  >({
    mutationFn: ({ memberId, data }) =>
      memberService.updateMemberStatus(memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() })
      queryClient.invalidateQueries({ queryKey: memberKeys.all })
      queryClient.invalidateQueries({ queryKey: memberKeys.stats() })
    }
  })
}

// Hook to delete a member
export function useDeleteMember() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { memberId: string; data: UpdateMember }
  >({
    mutationFn: ({ memberId }) =>
      memberService.deleteMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() })
      queryClient.invalidateQueries({ queryKey: memberKeys.all })
      queryClient.invalidateQueries({ queryKey: memberKeys.stats() })
    }
  })
}

// Hook to fetch member categories
export function useMemberCategories() {
  return useQuery<ApiResponse<MemberCategory[]>, Error>({
    queryKey: memberKeys.categories(),
    queryFn: () => memberService.getMemberCategories()
  })
}

// Hook to fetch published members count
export function usePublishedMembersCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...memberKeys.stats(), 'published'],
    queryFn: () => memberService.getPublishedMembersCount()
  })
}

// Hook to fetch draft members count
export function useDraftMembersCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...memberKeys.stats(), 'draft'],
    queryFn: () => memberService.getDraftMembersCount()
  })
}

// Hook to fetch members stats
export function useMembersStats() {
  return useQuery<MemberStatsResponse, Error, MemberStats>({
    queryKey: [...memberKeys.stats(), 'overview'],
    queryFn: () => memberService.getMembersStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}

// Hook to search members
export function useSearchMembers(
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<Member>>, Error>({
    queryKey: memberKeys.search(searchBy || '', params),
    queryFn: () => memberService.searchMembers(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook for infinite scroll members
export function useInfiniteMembers(baseFilters: MemberFilterParams = {}) {
  return useInfiniteQuery<ApiResponse<PaginatedResponse<Member>>, Error, ApiResponse<PaginatedResponse<Member>>, readonly unknown[], number>({
    queryKey: [...memberKeys.lists(), 'infinite', baseFilters],
    queryFn: ({ pageParam }) =>
      memberService.filterMembers({ page: pageParam as number, size: 10 }, baseFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.success || !lastPage.data) return undefined
      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 2 * 60 * 1000
  })
}

// Hook to get member category count for filters
export function useMembersCategoryCount() {
  return useQuery<ApiResponse<MembersCategoryCount>, Error>({
    queryKey: memberKeys.membersCategoryCount(),
    queryFn: () => memberService.filterMembersCategoryCount()
  })
}
